import { FC, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#region types
import { StyleEnums, FileEnums } from "models/enums";
import { MoreActionMenuItemType } from "models/propTypes/menuPropTypes";
import { ListMoreActionButtonProps } from "models/propTypes/wintSalaryPropTypes";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { TranslationKeys } from "translations";
import { FileConstants, IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModelWintSalaryReport } from "wint-api-client/dist/types";
//#endregion
//#region components
import { Icon, MenuButton, RoundButton, FileViewerContainer } from "components/common";
import { useWintSalaryService } from "serviceHooks";
import useSalaryChangeActions from "../useSalaryChangeActions";
import useEarlyPayoutDialog from "../useEarlyPayoutDialog";
import useEarlyPayoutPermissions from "../useEarlyPayoutPermissions";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const OPEN_PREVIEW_DELAY_MS = 50;

const ListMoreActionButton: FC<ListMoreActionButtonProps> = (props) => {
  const { personId, yearMonth, personName, paymentState, payslipId, onActionSuccess } = props;
  const salaryService = useWintSalaryService();
  const [attachments, setAttachments] = useState<ImageMeta[] | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const openPreviewRef = useRef<((index: number) => void) | undefined>(undefined);
  const { buildSalaryChangeMenuItems } = useSalaryChangeActions();
  const dispatch = useDispatch();
  const { canCreateEarlyPayout } = useEarlyPayoutPermissions();
  const { getEarlyPayoutDialog } = useEarlyPayoutDialog();

  const PreviewWrapper: React.FC<{ openPreview?: (index: number) => void }> = ({ openPreview }) => {
    useEffect(() => {
      if (openPreview) {
        openPreviewRef.current = openPreview;
      }
    }, [openPreview]);

    return <div style={{ display: "none" }} />;
  };

  const onPreviewClick = async () => {
    if (payslipId == null) return;
    setAttachments([
      {
        id: "loading",
        contentType: FileEnums.ContentTypes.ApplicationPDF,
        numberOfPages: 1,
        loading: true,
      },
    ]);
    setLoading(true);
    setTimeout(() => {
      openPreviewRef.current?.(0);
    }, OPEN_PREVIEW_DELAY_MS);

    try {
      const payslipImageData = await salaryService.getPayslipImageDataById(payslipId);
      setAttachments([payslipImageData]);
    } catch (error) {
      // Close preview dialog if it was opened
      document.getElementById(FileConstants.FileViewerFullScreenId)?.click();
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getMoreActionItems = () => {
    const { SalaryPaymentState } = WintSuperkollAPIModelWintSalaryReport;

    const zeroSalaryAllowedStates = [
      SalaryPaymentState.Unknown,
      SalaryPaymentState.NotPaid,
      SalaryPaymentState.NoPayment,
    ];

    const adjustSalaryAllowedStates = [
      ...zeroSalaryAllowedStates,
      SalaryPaymentState.PartiallyPaid,
      SalaryPaymentState.PaymentInProcess,
      SalaryPaymentState.SentToBank,
      SalaryPaymentState.CancelledByBank,
      // A payout that failed before reaching the bank is just as adjustable as one the bank
      // rejected — before DEV-27833 split the two, these reported CancelledByBank and were
      // already allowed here. Omitting it would hide "Justera lön" from precisely the
      // customers whose payout just failed and who need to correct the month.
      SalaryPaymentState.PayoutFailed,
    ];

    const canShowZero = paymentState === undefined || zeroSalaryAllowedStates.includes(paymentState);
    const canShowAdjust = paymentState === undefined || adjustSalaryAllowedStates.includes(paymentState);

    const salaryChangeItems = buildSalaryChangeMenuItems({
      personId,
      yearMonth,
      personName,
      onActionSuccess,
      hiddenZero: !canShowZero,
      hiddenAdjust: !canShowAdjust,
    });

    const moreactionsMenu: MoreActionMenuItemType[] = [
      {
        description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ViewPayslip),
        icon: IconDefinitionConstants.WEye,
        onClickHandler: onPreviewClick,
        hidden: payslipId == null,
      },
      ...salaryChangeItems,
      {
        description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Salary.ExtraPayoutMenuItemText),
        icon: IconDefinitionConstants.WMoney,
        onClickHandler: async () => {
          const employee = { id: personId, value: personName, title: personName };
          dispatch(displayDialog(getEarlyPayoutDialog(onActionSuccess, employee)));
        },
        hidden: (() => {
          if (!canCreateEarlyPayout) return true;
          const today = DateHelper.today();
          const currentYm = DateHelper.getYearMonth(today);
          const tenDaysLaterYm = DateHelper.getYearMonth(DateHelper.addDays(today, 10));
          return yearMonth !== currentYm && yearMonth !== tenDaysLaterYm;
        })(),
      },
    ];
    return moreactionsMenu;
  };

  return (
    <>
      <FileViewerContainer meta={attachments} parentId={1} loading={loading}>
        <PreviewWrapper />
      </FileViewerContainer>

      <MenuButton
        menuItems={getMoreActionItems()}
        menuPlacement="bottom-end"
        disablePopperPortal={false}
        popperOffSet={[15, 5]}
        button={
          <RoundButton size="xs" displayType={StyleEnums.DisplayType.None} className={cssExports.listMoreActions}>
            <Icon icon={IconDefinitionConstants.WEllipsisH} />
          </RoundButton>
        }
        classes={{
          menu: cssExports.wintSalaryListMoreActionsMenu,
          menuOpen: cssExports.listMoreActionsMenuOpen,
        }}
        onActionSuccess={onActionSuccess}
      />
    </>
  );
};
export default ListMoreActionButton;
